import React, { useState, useEffect, useContext } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { Layout, theme, Button, Divider, Badge } from 'antd';
import Icon, {
	BellFilled,
	BellOutlined,
	CloseOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
} from '@ant-design/icons';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Database } from '../../firebase';
import { AppContext } from '../../context';
import BlockLoading from '../../components/BlockLoading';

import AvatarName from '../../components/AvatarName';
import AppInformationTicker from '../../components/AppInformationTicker';
import CutomMenu from './components/CutomMenu';
import CustomBreadCrumb from './components/CustomBreadCrumb';

import { ReactComponent as HumburgerSvg } from '../../assets/icons/humberger.svg';
import useGetScreenSize from '../../hooks/useGetScreenSize';
import useAvailableScreenHeight from '../../hooks/useAvailableScreenHeight';

import Logo from '../../assets/images/cadira-logo-transparent.png';

import './styles.scss';

const { Header, Content, Sider } = Layout;

const App = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const screenHeight = useGetScreenSize();
	const isTabletOrMobile = useMediaQuery({ query: '(max-width: 900px)' });
	const {
		session: { status, name, profilePicture, userId },
		needsNotifPermission,
		requestNotifPermission,
	} = useContext(AppContext);
	const brandHomePath = status === 'authenticated' ? '/beranda' : '/';
	const [notifBannerDismissed, setNotifBannerDismissed] = useState(false);
	const showNotifBanner = needsNotifPermission && !notifBannerDismissed;
	const [unreadCount, setUnreadCount] = useState(0);
	const [targetRef, totalAvailableHeight] =
		useAvailableScreenHeight(!isTabletOrMobile);

	const [collapsed, setCollapsed] = useState(true);

	useEffect(() => {
		setCollapsed(isTabletOrMobile);
	}, [isTabletOrMobile]);

	useEffect(() => {
		const isTestRunnerRoute =
			pathname.includes('/kerjakan-test') || pathname.includes('/mulai-test');
		if (isTestRunnerRoute) {
			setCollapsed(true);
		}
	}, [pathname]);
	const {
		token: { borderRadiusLG, colorPrimary },
	} = theme.useToken();

	// session guard: redirect jika tidak terautentikasi
	useEffect(() => {
		if (status === 'no-authenticated') {
			navigate('/');
		}
	}, [status, navigate]);

	// Real-time unread notification count
	useEffect(() => {
		if (!userId) return undefined;
		const q = query(
			collection(Database, 'notificationLogs'),
			where('userId', '==', userId),
			where('isRead', '==', false)
		);
		const unsub = onSnapshot(q, (snap) => setUnreadCount(snap.size));
		return () => unsub();
	}, [userId]);

	// ✅ Tampilkan loading saat status masih dicek — cegah flash konten protected
	if (status === 'checking') {
		return (
			<div
				style={{
					width: '100vw',
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<BlockLoading />
			</div>
		);
	}

	return (
		<Layout
			className="menu-layout-container"
			style={{ minHeight: screenHeight }}
		>
			<Sider
				width={isTabletOrMobile ? 250 : 256}
				collapsible
				collapsed={collapsed}
				onCollapse={(value) => setCollapsed(value)}
				trigger={null}
				collapsedWidth={isTabletOrMobile ? 0 : 80}
				className={clsx('layout-seider', { mobile: isTabletOrMobile })}
				style={{ height: screenHeight }}
			>
				<div className="name-logo-container">
					<Link to={brandHomePath} className="brand-home-link">
						<img src={Logo} width={32} height={32} alt="cadira" />
						{!collapsed && <p className="title">Cadira</p>}
					</Link>
					{isTabletOrMobile && (
						<Button
							type="text"
							className="sider-close-button"
							icon={<MenuFoldOutlined />}
							onClick={() => setCollapsed(true)}
						/>
					)}
				</div>

				<div className="sider-menu-scroll">
					<CutomMenu
						collapse={collapsed}
						onCollapse={(val) => setCollapsed(val)}
					/>
				</div>
			</Sider>

			{!collapsed && isTabletOrMobile && (
				<div
					className="sider-overlay"
					role="button"
					tabIndex={0}
					aria-label="Tutup menu"
					onClick={() => setCollapsed(true)}
					onKeyDown={(e) => {
						if (e.key === 'Escape') setCollapsed(true);
					}}
				/>
			)}

			<Layout>
				<Header>
					<div className="header-shell full-height">
						<div className="header-left">
							{isTabletOrMobile ? (
								<>
									<Button
										type="text"
										className="toogle-show-sider"
										icon={<Icon component={HumburgerSvg} />}
										onClick={() => setCollapsed(false)}
									/>
									<Link to={brandHomePath} className="mobile-header-brand">
										<img src={Logo} width={28} height={28} alt="Cadira" />
										<span>Cadira</span>
									</Link>
								</>
							) : (
								<Button
									type="text"
									className="toogle-show-sider"
									icon={
										collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
									}
									onClick={() => setCollapsed(!collapsed)}
								/>
							)}
						</div>
						<div className="header-menu">
							<AvatarName
								name={!collapsed && isTabletOrMobile ? null : name}
								photoUrl={profilePicture}
							/>
							<Divider type="vertical" />
							<Badge count={unreadCount} size="small" offset={[0, 18]}>
								<Button
									type="text"
									className="toogle-collapse-button"
									icon={<BellFilled />}
									style={{
										fontSize: isTabletOrMobile ? '18px' : '20px',
										width: 20,
										height: 64,
										color: colorPrimary,
									}}
									onClick={() => navigate('/beranda/notifikasi')}
								/>
							</Badge>
						</div>
					</div>
				</Header>
				<AppInformationTicker />
				<Content
					style={{
						margin: '0 16px',
					}}
				>
					{showNotifBanner && (
						<div className="notif-permission-banner">
							<div className="notif-permission-banner__content">
								<BellOutlined className="notif-permission-banner__icon" />
								<span>
									Aktifkan notifikasi untuk menerima update latihan dan ujian
								</span>
							</div>
							<div className="notif-permission-banner__actions">
								<Button
									type="primary"
									size="small"
									onClick={requestNotifPermission}
								>
									Aktifkan
								</Button>
								<Button
									type="text"
									size="small"
									icon={<CloseOutlined />}
									onClick={() => setNotifBannerDismissed(true)}
									className="notif-permission-banner__close"
								/>
							</div>
						</div>
					)}

					<div style={{ margin: '16px 0' }}>
						<CustomBreadCrumb />
					</div>

					<div
						ref={targetRef}
						style={{
							borderRadius: borderRadiusLG,
							overflowY: !isTabletOrMobile ? 'auto' : 'hidden',
							height: totalAvailableHeight,
						}}
					>
						<Outlet />
					</div>
				</Content>
			</Layout>
		</Layout>
	);
};
export default App;
